import { AndroidInterface } from "@/apps/credit-line/services/android.service";
declare const Android: AndroidInterface;
import { isAndroid } from "@/apps/credit-line/helpers/platformChecker.helper";

let pendingResolve: ((value: boolean) => void) | null = null;
let pendingReject: ((reason?: unknown) => void) | null = null;

export enum BiometricResultCode {
  BIOMETRIC_SUCCESS = 0,
  // The rest we treat as failure for now
  ERROR_HW_UNAVAILABLE = 1,
  ERROR_UNABLE_TO_PROCESS = 2,
  ERROR_TIMEOUT = 3,
  ERROR_NO_SPACE = 4,
  ERROR_CANCELED = 5,
  ERROR_LOCKOUT = 7,
  ERROR_VENDOR = 8,
  ERROR_LOCKOUT_PERMANENT = 9,
  ERROR_USER_CANCELED = 10,
  ERROR_NO_BIOMETRICS = 11,
  ERROR_HW_NOT_PRESENT = 12,
  ERROR_NEGATIVE_BUTTON = 13,
  ERROR_NO_DEVICE_CREDENTIAL = 14,
  ERROR_SECURITY_UPDATE_REQUIRED = 15,
}

/**
 * Called whenever we want to trigger biometric auth.
 * Returns a Promise that resolves to:
 *   true  -> BIOMETRIC_SUCCESS (0)
 *   false -> any other code OR failure in calling native
 */
export function requestBiometricAuth(): Promise<boolean> {
  console.log("Requesting biometric authentication");
  return new Promise((resolve, reject) => {
    // If there's already a pending request, reject the previous one
    if (pendingResolve || pendingReject) {
      pendingReject?.(new Error("Previous biometric request superseded"));
    }

    pendingResolve = resolve;
    pendingReject = reject;

    if (
      isAndroid() &&
      typeof Android !== "undefined" &&
      typeof Android.requestBiometricAuthentication === "function"
    ) {
      try {
        Android.requestBiometricAuthentication();
      } catch (err) {
        // Native call failed; fail the request
        pendingReject?.(err);
        pendingResolve = null;
        pendingReject = null;
      }
    } else {
      // Biometric required but not supported.
      // SECURITY-FIRST DEFAULT: reject / return false.
      pendingResolve(false);
      pendingResolve = null;
      pendingReject = null;
    }
  });
}

/**
 * Internal function that will be hooked into window.creditLineApp
 * and called by the native layer.
 */
function internalBiometricAuthCallback(resultCode: number) {
  console.log("Biometric auth callback received", { resultCode });
  if (!pendingResolve) {
    console.warn("Biometric callback received but no pending request", {
      resultCode,
    });
    return;
  }

  const ok = resultCode === BiometricResultCode.BIOMETRIC_SUCCESS;

  pendingResolve(ok);
  pendingResolve = null;
  pendingReject = null;
}

// --- Register callback on window.creditLineApp ---

// Merge with any existing creditLineApp object
window.creditLineApp = {
  ...(window.creditLineApp || {}),
  biometricAuthCallback: internalBiometricAuthCallback,
};
